import { usePrivy } from '@privy-io/react-auth';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getPrivyPrimaryAddress } from '@/hooks';
import { getPrivateApiErrorInterceptor } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { ErrorCode, getErrorMsg } from '@/types';

import { NotificationChannel } from './get-notifications-settings';

type Response = { success: boolean };
type Body = Pick<NotificationChannel, 'channelId'>;
interface Data extends Response {
  delayInSecs: number;
}

async function sendChannelCode(
  body: Body,
  address: string | undefined,
): Promise<Data> {
  if (!address) {
    toast.error('Wallet address not found. Please reconnect your wallet.');
    return { success: false, delayInSecs: 0 };
  }
  try {
    const apiKey = useAccountStore.getState().getAuthParams(address)?.apiKey;
    const { data } = await axios.post<Response>(
      import.meta.env.VITE_REST_V2_URL + '/v2/notifications/email/send-code',
      body,
      {
        headers: {
          'X-API-KEY': apiKey,
        },
      },
    );
    return { ...data, delayInSecs: 30 };
  } catch (err) {
    const error = err as AxiosError<{ code: ErrorCode }>;
    const code = error.response?.data.code;
    if (code === ErrorCode.TOO_MANY_REQUESTS) {
      const header =
        error.response?.headers['x-ratelimit-verificationcode-retryafter'];
      const secondsLeft = parseInt(header);
      const delayInSecs = isNaN(secondsLeft) ? 30 : secondsLeft;
      toast.error(
        `Too many attempts. Please try again in ${delayInSecs} seconds.`,
      );
      return { success: false, delayInSecs };
    } else {
      return getPrivateApiErrorInterceptor({ isPrimary: true })(error);
    }
  }
}

export default function useSendChannelCode() {
  const { user } = usePrivy();
  const { t } = useTranslation();
  const [delayInSecs, setDelayInSecs] = useState(0);

  useEffect(() => {
    if (delayInSecs === 0) return;
    const timeoutId = window.setTimeout(
      () => setDelayInSecs((prev) => prev - 1),
      1_000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [delayInSecs]);

  const mutation = useMutation<Data, ErrorCode, Body>({
    mutationFn: (body: Body) =>
      sendChannelCode(body, getPrivyPrimaryAddress(user)),
    onSuccess: ({ delayInSecs }) => setDelayInSecs(delayInSecs),
    onError: (err) => toast.error(getErrorMsg(err, t)),
  });

  return { ...mutation, delayInSecs };
}
